/**
 * Graphing V2 — Dashboard ("your files" screen).
 *
 * A separate, app-like screen from the marketing landing: this is where the user's
 * persistent projects live (create / open / rename / delete), Figma-files style.
 * Shares the blueprint `.gv2` theme; project-card styles live in DemoGallery.css.
 */

import { useRef, useState } from 'react'
import { relativeTime, type Project } from '../projects'
import './DemoGallery.css'

interface DashboardProps {
  projects: Project[]
  onOpenProject: (project: Project) => void
  onNewProject: () => void
  onRenameProject: (id: string, name: string) => void
  onDeleteProject: (id: string) => void
  onHome: () => void
  onExploreShowcase: () => void
}

const TYPE_LABEL: Record<string, string> = {
  architecture: 'ARCHITECTURE',
  erd: 'ERD',
  class: 'UML CLASS',
  state: 'STATE MACHINE',
  c4: 'C4 MODEL',
  sequence: 'SEQUENCE',
  gantt: 'GANTT',
  flowchart: 'FLOWCHART',
}

const TYPE_CODE: Record<string, string> = {
  architecture: 'ARCH',
  erd: 'ERD',
  class: 'UML',
  state: 'STATE',
  c4: 'C4',
  sequence: 'SEQ',
  gantt: 'GANTT',
  flowchart: 'FLOW',
}

export default function Dashboard({
  projects,
  onOpenProject,
  onNewProject,
  onRenameProject,
  onDeleteProject,
  onHome,
  onExploreShowcase,
}: DashboardProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  const startRename = (p: Project) => {
    setRenamingId(p.id)
    setDraft(p.name)
  }
  const commitRename = () => {
    if (renamingId && draft.trim()) onRenameProject(renamingId, draft)
    setRenamingId(null)
  }

  return (
    <div className="gv2 gv2-dashboard" ref={rootRef}>
      <div className="gv2-grid-bg" aria-hidden />
      <div className="gv2-glow" aria-hidden />

      {/* Top bar */}
      <nav className="gv2-nav">
        <button className="gv2-wordmark gv2-wordmark-btn" onClick={onHome} title="Back to home">
          <span className="gv2-mark" aria-hidden>◳</span>
          Graphing<span className="gv2-mark-accent">V2</span>
        </button>
        <div className="gv2-nav-right">
          <button className="gv2-nav-link" onClick={onExploreShowcase}>Showcase</button>
          <button className="gv2-nav-btn" onClick={onNewProject}>New project +</button>
        </div>
      </nav>

      <main className="gv2-dash-main">
        <header className="gv2-dash-head">
          <div>
            <div className="gv2-kicker">// WORKSPACE</div>
            <h1 className="gv2-dash-title">Your projects</h1>
          </div>
          <span className="gv2-dash-count">
            {projects.length
              ? `${projects.length} project${projects.length === 1 ? '' : 's'} · saved in this browser`
              : 'no projects yet'}
          </span>
        </header>

        <div className="gv2-proj-grid">
          {/* New project card */}
          <button className="gv2-proj gv2-proj-new" onClick={onNewProject} title="New project">
            <span className="gv2-proj-plus" aria-hidden>+</span>
            <span className="gv2-proj-new-label">New project</span>
            <span className="gv2-proj-new-sub">Blank canvas — add any diagram type</span>
          </button>

          {projects.map((p) => (
            <div
              key={p.id}
              className="gv2-proj gv2-proj-card"
              onClick={() => renamingId !== p.id && onOpenProject(p)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' && renamingId !== p.id) onOpenProject(p) }}
              title={`Open "${p.name}"`}
            >
              <div className={`gv2-proj-tile gv2-tile-${p.diagramType}`} aria-hidden>
                <span className="gv2-proj-code">{TYPE_CODE[p.diagramType] ?? 'DIAGRAM'}</span>
              </div>
              <div className="gv2-proj-body">
                {renamingId === p.id ? (
                  <input
                    className="gv2-proj-rename"
                    value={draft}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename()
                      if (e.key === 'Escape') setRenamingId(null)
                    }}
                  />
                ) : (
                  <h3 className="gv2-proj-name">{p.name}</h3>
                )}
                <span className="gv2-proj-meta">
                  {TYPE_LABEL[p.diagramType] ?? p.diagramType} · {relativeTime(p.updatedAt)}
                </span>
              </div>
              <div className="gv2-proj-actions">
                <button
                  className="gv2-proj-act"
                  title="Rename"
                  onClick={(e) => { e.stopPropagation(); startRename(p) }}
                >✎</button>
                <button
                  className="gv2-proj-act gv2-proj-del"
                  title="Delete"
                  onClick={(e) => { e.stopPropagation(); onDeleteProject(p.id) }}
                >×</button>
              </div>
            </div>
          ))}
        </div>

        {projects.length === 0 && (
          <p className="gv2-dash-empty-hint">
            Spin up a blank canvas with <strong>New project</strong>, or start from a
            <button className="gv2-inline-link" onClick={onExploreShowcase}>showcase template</button>.
          </p>
        )}
      </main>
    </div>
  )
}
